import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { AGREEMENT_COUNTDOWN_SECONDS } from "../constants";
import LanguageSwitcher from "./language-switcher";

export default function EventRegisterAgreementModal({
    open,
    onAgree,
    onCancel,
    submitting,
}) {
    const { t } = useTranslation();
    const [secondsLeft, setSecondsLeft] = useState(AGREEMENT_COUNTDOWN_SECONDS);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        setSecondsLeft(AGREEMENT_COUNTDOWN_SECONDS);
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
        const interval = setInterval(() => {
            setSecondsLeft((s) => {
                if (s <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [open]);

    const canAgree = secondsLeft <= 0 && !submitting;

    const agreeLabel = useMemo(() => {
        if (submitting) return t("eventRegister.agreement.recording");
        if (secondsLeft > 0) {
            return t("eventRegister.agreement.iAgreeCountdown", {
                seconds: secondsLeft,
            });
        }
        return t("eventRegister.agreement.iAgree");
    }, [submitting, secondsLeft, t]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4">
            <div className="bg-base-100 border w-full max-w-2xl max-h-[90vh] flex flex-col relative">
                <LanguageSwitcher className="absolute top-2 right-2 z-10" />
                <div className="p-4 border-b">
                    <p className="font-bold text-lg">
                        {t("eventRegister.agreement.title")}
                    </p>
                    <p className="opacity-80 text-sm mt-1">
                        {t("eventRegister.agreement.subtitle")}
                    </p>
                </div>
                <div
                    ref={scrollRef}
                    className="p-4 overflow-y-auto flex-1 flex flex-col gap-3 text-sm"
                >
                    <p className="font-bold">{t("fighterConsent.title")}</p>
                    <p>{t("fighterConsent.acknowledgment")}</p>
                    <p>{t("fighterConsent.assumptionOfRisk")}</p>
                    <p>{t("fighterConsent.medicalCondition")}</p>
                    <p>{t("fighterConsent.trainingAndPreparation")}</p>
                    <p>{t("fighterConsent.complianceWithRules")}</p>
                    <p>{t("fighterConsent.releaseOfLiability")}</p>
                    <p>{t("fighterConsent.insurance")}</p>
                    <p>{t("fighterConsent.mediaRelease")}</p>
                    <p>{t("fighterConsent.confirmation")}</p>

                    <p className="font-bold pt-2">
                        {t("fighterConsent.eventParticipationTitle")}
                    </p>
                    <ul className="list-disc list-outside pl-5 flex flex-col gap-2">
                        <li>{t("fighterConsent.eventInfo")}</li>
                        <li>{t("fighterConsent.withdrawalPolicy")}</li>
                        <li>{t("fighterConsent.refundPolicy")}</li>
                        <li>{t("fighterConsent.matchmaking")}</li>
                        <li>{t("fighterConsent.minorPolicy")}</li>
                    </ul>
                    <p>{t("fighterConsent.closing")}</p>
                </div>
                <div className="p-4 border-t flex flex-col gap-2">
                    {secondsLeft > 0 && (
                        <p className="text-sm opacity-80 text-center">
                            {t("eventRegister.agreement.readPrompt", {
                                seconds: secondsLeft,
                            })}
                        </p>
                    )}
                    <div className="flex flex-row gap-2">
                        <button
                            type="button"
                            className="btn btn-outline flex-1"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            {t("eventRegister.agreement.cancel")}
                        </button>
                        <button
                            type="button"
                            className={twMerge(
                                "btn flex-1",
                                canAgree ? "btn-primary" : "btn-disabled"
                            )}
                            onClick={canAgree ? onAgree : undefined}
                            disabled={!canAgree}
                        >
                            {agreeLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
